import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import Input from '../components/ui/Input';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { organizationsService } from '../services/organizationsService';
import {
  offerStudioService,
  encodeRules,
  decodeRules,
  ruleFromFinding,
  ruleKey,
  readLastOffer,
  writeLastOffer,
} from '../services/offerStudioService';
import KpiStrip from '../components/offerStudio/KpiStrip';
import BaselineSelect from '../components/offerStudio/BaselineSelect';
import CategoryNav from '../components/offerStudio/CategoryNav';
import FeatureCard from '../components/offerStudio/FeatureCard';
import InsightList from '../components/offerStudio/InsightList';
import RulesPanel from '../components/offerStudio/RulesPanel';
import { fmtDate, fmtInt } from '../components/offerStudio/format';

const BASELINE_SHORT = { universe: 'universe', clickers: 'clickers', vertical: 'vertical' };

function OfferPicker({ offers, value, onChange, loading }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return offers;
    return offers.filter((o) => String(o.offer_id) === t || (o.offer_name || '').toLowerCase().includes(t));
  }, [offers, q]);
  const current = offers.find((o) => String(o.offer_id) === String(value));
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter offers by ID or name…"
          className="pl-8"
          aria-label="Filter offers"
        />
      </div>
      <FunctionalSelect
        label="Offer"
        value={value ? String(value) : ''}
        onValueChange={onChange}
        placeholder={loading ? 'Loading offers…' : 'Pick an offer'}
        disabled={loading}
        className="w-full sm:w-[28rem]"
      >
        {filtered.slice(0, 200).map((o) => (
          <SelectItem key={o.offer_id} value={String(o.offer_id)}>
            <span className={o.has_converter_data === false ? 'opacity-60' : ''}>
              <span className="tabular-nums text-gray-500 mr-2">{o.offer_id}</span>
              {o.offer_name || '—'}
              <span className="ml-2 text-xs text-gray-400">
                {o.category || ''}
                {o.converters != null ? ` · ${fmtInt(o.converters)} conv` : o.total_click != null ? ` · ${fmtInt(o.total_click)} clicks` : ''}
                {o.has_converter_data === false ? ' · no converter data' : ''}
              </span>
            </span>
          </SelectItem>
        ))}
        {filtered.length === 0 ? <div className="px-2 py-1 text-sm text-gray-500">No offers match</div> : null}
      </FunctionalSelect>
      {current ? (
        <div className="text-xs text-gray-500 sm:pb-2">
          {current.advertiser_name ? `${current.advertiser_name} · ` : ''}
          {current.category || 'no vertical'}
        </div>
      ) : null}
    </div>
  );
}

export default function OfferAudienceStudio() {
  const { offerId: offerIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const offerId = offerIdParam || '';
  const baseline = searchParams.get('baseline') || 'universe';
  const category = searchParams.get('category') || '';
  const [rules, setRules] = useState(() => decodeRules(searchParams.get('rules')));
  const [match, setMatch] = useState(searchParams.get('match') === 'ANY' ? 'ANY' : 'ALL');

  const [overview, setOverview] = useState(null);
  const [features, setFeatures] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ---- URL state helpers ----------------------------------------------------
  const setParam = useCallback(
    (key, val) => {
      const next = new URLSearchParams(searchParams);
      if (val) next.set(key, val);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const enc = encodeRules(rules);
    if (enc) next.set('rules', enc);
    else next.delete('rules');
    if (match === 'ANY') next.set('match', 'ANY');
    else next.delete('match');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, match]);

  // ---- organizations --------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        setOrganizations(orgs);
        const saved = organizationsService.getSelectedOrganization('id');
        const pick = saved && orgs.some((o) => String(o.id) === String(saved)) ? String(saved) : orgs[0] ? String(orgs[0].id) : '';
        setOrganizationId(pick);
      } catch {
        if (!cancelled) setError('Failed to load organizations.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- offers ---------------------------------------------------------------
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setOffersLoading(true);
    (async () => {
      try {
        const list = await offerStudioService.listOffers(organizationId);
        if (cancelled) return;
        setOffers(list);
        if (!offerId) {
          const last = readLastOffer();
          const pick = list.find((o) => String(o.offer_id) === last) || list[0];
          if (pick) navigate(`/offer-report/${pick.offer_id}?${searchParams.toString()}`, { replace: true });
        }
      } catch {
        if (!cancelled) setError('Failed to load offers.');
      } finally {
        if (!cancelled) setOffersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // ---- report data ----------------------------------------------------------
  useEffect(() => {
    if (!organizationId || !offerId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    writeLastOffer(offerId);
    (async () => {
      try {
        const ov = await offerStudioService.overview(offerId, organizationId, baseline);
        if (cancelled) return;
        setOverview(ov);
        const [feats, ins] = await Promise.all([
          offerStudioService.features(offerId, organizationId, baseline, category),
          offerStudioService.insights(offerId, organizationId, baseline, 12),
        ]);
        if (cancelled) return;
        setFeatures(feats);
        setInsights(ins);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 400 && baseline !== 'universe') {
          setParam('baseline', '');
          return;
        }
        setError(typeof detail === 'string' ? detail : 'Failed to load the offer report.');
        setOverview(null);
        setFeatures([]);
        setInsights([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, offerId, baseline, category]);

  // ---- rules ----------------------------------------------------------------
  const ruleKeys = useMemo(() => new Set(rules.map(ruleKey)), [rules]);
  const isRuleActive = useCallback((featureOrInsight, value) => {
    const f = value ? featureOrInsight : { feature: featureOrInsight.feature };
    const v = value || { value: featureOrInsight.value };
    return ruleKeys.has(`${f.feature}::${v.value}`);
  }, [ruleKeys]);

  const toggleRule = useCallback((featureOrInsight, value) => {
    const feature = value
      ? featureOrInsight
      : { feature: featureOrInsight.feature, display_label: featureOrInsight.display_label, segment_column: featureOrInsight.segment_column };
    const v = value || {
      value: featureOrInsight.value,
      value_label: featureOrInsight.value_label,
      index: featureOrInsight.index,
      segment_condition: featureOrInsight.segment_condition,
    };
    const rule = ruleFromFinding(feature, v);
    const key = ruleKey(rule);
    setRules((prev) => (prev.some((r) => ruleKey(r) === key) ? prev.filter((r) => ruleKey(r) !== key) : [...prev, rule]));
  }, []);

  const removeRule = useCallback((rule) => setRules((prev) => prev.filter((r) => ruleKey(r) !== ruleKey(rule))), []);

  const buildSegment = useCallback(() => {
    const enc = encodeRules(rules);
    const q = new URLSearchParams({ offer: String(offerId), rules: enc, match });
    navigate(`/segments/new?${q.toString()}`);
  }, [rules, offerId, match, navigate]);

  const presets = useMemo(() => insights.filter((i) => i.direction === 'over').slice(0, 4), [insights]);

  const baselineLabel = BASELINE_SHORT[baseline] || baseline;
  const offer = overview?.offer;
  const computed = fmtDate(overview?.computed_at);

  const jumpToFeature = useCallback(
    (i) => {
      if (i.category && i.category !== category) setParam('category', i.category);
      window.setTimeout(() => {
        document.getElementById(`feature-${i.feature}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    },
    [category, setParam]
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/offer-report')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft className="h-4 w-4" /> Offer list
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Offer audience</h1>
          {offer ? (
            <span className="text-sm text-gray-500 truncate">
              — {offer.offer_name || offer.offer_id}
              {offer.category ? ` · ${offer.category}` : ''}
              {computed ? ` · computed ${computed}` : ''}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          {organizations.length > 1 ? (
            <FunctionalSelect
              label="Organization"
              value={organizationsService.getSelectedOptionValue(organizations, organizationId, 'id')}
              onValueChange={(value) => {
                const parsed = organizationsService.parseOrganizationOptionValue(value);
                const id = parsed?.id != null ? String(parsed.id) : String(value);
                setOrganizationId(id);
                organizationsService.setSelectedOrganization(id, 'id', organizations);
              }}
              className="w-full lg:w-56"
            >
              {organizations.map((o) => (
                <SelectItem key={o.id} value={organizationsService.getSelectedOptionValue(organizations, String(o.id), 'id')}>
                  {o.name}
                </SelectItem>
              ))}
            </FunctionalSelect>
          ) : null}
          <OfferPicker
            offers={offers}
            value={offerId}
            loading={offersLoading}
            onChange={(id) => navigate(`/offer-report/${id}?${new URLSearchParams({ baseline }).toString()}`)}
          />
          <BaselineSelect value={baseline} onChange={(b) => setParam('baseline', b === 'universe' ? '' : b)} available={overview?.available_baselines} offerCategory={offer?.category} />
        </div>
      </div>

      {error ? <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {overview && overview.has_converter_data === false ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This offer has no converter distribution yet, so only the {baselineLabel} side can be shown. The metrics
          job left converter shares empty for it; they will be derived from converter counts once the updated
          load_offers job has run.
        </div>
      ) : null}

      <KpiStrip overview={overview} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4">
        {/* Left rail */}
        <aside className="space-y-3">
          <RulesPanel
            rules={rules}
            match={match}
            onMatchChange={setMatch}
            onRemove={removeRule}
            onReset={() => setRules([])}
            onBuildSegment={buildSegment}
            presets={presets}
            onAddPreset={(p) => toggleRule(p)}
            isRuleActive={(p) => isRuleActive(p)}
          />
          <CategoryNav categories={overview?.categories || []} selected={category} onSelect={(c) => setParam('category', c)} />
          {overview?.data_notes?.length ? (
            <ul className="text-[11px] text-gray-500 space-y-1 px-1">
              {overview.data_notes.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          ) : null}
        </aside>

        {/* Main */}
        <main className="space-y-4 min-w-0">
          {!category ? (
            <section className="rounded-lg border bg-white p-4" aria-label="Insights">
              <h2 className="text-sm font-semibold text-gray-900 mb-1">
                What sets converters apart <span className="text-gray-400 font-normal">vs {baselineLabel}</span>
              </h2>
              <InsightList insights={insights} baselineLabel={baselineLabel} hasConverterData={overview?.has_converter_data !== false} loading={loading && !insights.length} onAddRule={(i) => toggleRule(i)} isRuleActive={(i) => isRuleActive(i)} onJumpToFeature={jumpToFeature} />
            </section>
          ) : null}

          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              {category ? overview?.categories?.find((c) => c.id === category)?.label || 'Category' : 'All features'}
              <span className="ml-2 text-gray-400 font-normal">{features.length} feature{features.length === 1 ? '' : 's'} · sorted by distinctiveness</span>
            </h2>
          </div>

          {loading && !features.length ? <div className="text-sm text-gray-500">Loading…</div> : null}
          {!loading && !features.length && offerId ? <div className="text-sm text-gray-500">No feature data for this offer.</div> : null}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.feature} id={`feature-${f.feature}`}>
                <FeatureCard feature={f} baselineLabel={baselineLabel} onAddRule={toggleRule} isRuleActive={isRuleActive} />
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
